import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faRotate, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';
import { useBookSymbols } from '../hooks/useBookSymbols';
import { useQuote } from '../contexts/QuoteContext';

const formatPct = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
};

const pctClass = (value) => {
  if (value == null || Number.isNaN(Number(value))) return 'text-muted';
  return Number(value) >= 0 ? 'text-bull' : 'text-bear';
};

function barReturn(bars, lookback) {
  const list = Array.isArray(bars) ? bars : [];
  if (list.length < 2) return null;
  const latest = list[list.length - 1];
  const prior = list[Math.max(0, list.length - 1 - lookback)];
  const close = Number(latest?.close ?? latest?.c);
  const base = Number(prior?.close ?? prior?.c);
  if (!Number.isFinite(close) || !Number.isFinite(base) || base === 0) return null;
  return ((close - base) / base) * 100;
}

const RelativeStrength = ({ onSymbolSelect }) => {
  const { symbol: quoteSymbol, selectSymbol } = useQuote();
  const { symbols: bookSymbols, loading: bookLoading } = useBookSymbols();
  const [symbol, setSymbol] = useState('');
  const [horizon, setHorizon] = useState('day');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const choices = useMemo(() => {
    const set = new Set(bookSymbols);
    if (quoteSymbol) set.add(String(quoteSymbol).toUpperCase());
    if (symbol) set.add(String(symbol).toUpperCase());
    return [...set].sort();
  }, [bookSymbols, quoteSymbol, symbol]);

  useEffect(() => {
    if (quoteSymbol) {
      setSymbol(String(quoteSymbol).toUpperCase());
      return;
    }
    if (!symbol && bookSymbols[0]) {
      setSymbol(bookSymbols[0]);
    }
  }, [quoteSymbol, bookSymbols, symbol]);

  const load = async () => {
    if (!symbol) return;
    try {
      setLoading(true);
      setError('');
      const lookback = horizon === 'week' ? 5 : 1;
      const limit = horizon === 'week' ? 12 : 5;
      const [stockRes, spyRes, qqqRes] = await Promise.all([
        marketDataApi.getBars(symbol, { timeframe: '1Day', limit }),
        marketDataApi.getBars('SPY', { timeframe: '1Day', limit }),
        marketDataApi.getBars('QQQ', { timeframe: '1Day', limit }),
      ]);

      const stockRet = barReturn(stockRes.data, lookback);
      const spyRet = barReturn(spyRes.data, lookback);
      const qqqRet = barReturn(qqqRes.data, lookback);

      setRows({
        symbol: symbol.toUpperCase(),
        stockRet,
        spyRet,
        qqqRet,
        vsSpy: stockRet != null && spyRet != null ? stockRet - spyRet : null,
        vsQqq: stockRet != null && qqqRet != null ? stockRet - qqqRet : null,
      });
    } catch (err) {
      console.error('Relative strength error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load RS');
      setRows(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, horizon]);

  const open = (sym) => {
    if (onSymbolSelect) onSymbolSelect(sym);
    else selectSymbol(sym);
  };

  return (
    <div className="lt-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-hairline">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faChartLine} className="text-xs text-muted" />
            <span className="lt-label">Relative strength</span>
          </div>
          <div className="text-[11px] text-muted mt-0.5">vs SPY / QQQ</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <select
            value={horizon}
            onChange={(e) => setHorizon(e.target.value)}
            className="h-8 px-2 text-[11px] lt-input"
          >
            <option value="day">1D</option>
            <option value="week">1W</option>
          </select>
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="h-8 px-2 text-[11px] lt-input max-w-[96px]"
            disabled={bookLoading && !choices.length}
          >
            {!choices.length && <option value="">—</option>}
            {choices.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading || !symbol}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated disabled:opacity-50"
            title="Refresh"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faRotate} className={`text-xs ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-3 px-2.5 py-2 rounded-md text-xs bg-[var(--lt-bear-dim)] text-bear border border-[rgba(240,113,103,0.28)]">
          {error}
        </div>
      )}

      <div className="px-3 py-3">
        {!symbol ? (
          <div className="text-xs text-muted text-center py-4">
            Select a symbol from your book or open a quote.
          </div>
        ) : loading && !rows ? (
          <div className="text-xs text-muted text-center py-4">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            Computing...
          </div>
        ) : rows ? (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => open(rows.symbol)}
              className="w-full text-left rounded-md border border-hairline bg-canvas px-3 py-2 hover:bg-elevated"
            >
              <div className="lt-label mb-1">{rows.symbol}</div>
              <div className={`num text-lg font-semibold ${pctClass(rows.stockRet)}`}>
                {formatPct(rows.stockRet)}
              </div>
            </button>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md border border-hairline bg-canvas px-3 py-2">
                <div className="lt-label mb-1">vs SPY</div>
                <div className={`num text-sm font-semibold ${pctClass(rows.vsSpy)}`}>
                  {formatPct(rows.vsSpy)}
                </div>
                <div className="text-[10px] text-muted mt-0.5">
                  SPY {formatPct(rows.spyRet)}
                </div>
              </div>
              <div className="rounded-md border border-hairline bg-canvas px-3 py-2">
                <div className="lt-label mb-1">vs QQQ</div>
                <div className={`num text-sm font-semibold ${pctClass(rows.vsQqq)}`}>
                  {formatPct(rows.vsQqq)}
                </div>
                <div className="text-[10px] text-muted mt-0.5">
                  QQQ {formatPct(rows.qqqRet)}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-muted text-center py-4">No bar data</div>
        )}
      </div>
    </div>
  );
};

export default RelativeStrength;
